import { API_BASE_URL, STORAGE_KEYS } from '@/config/api';
import type { ApiError } from '@/types';

// Interface para opções de requisição
interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

// Classe para erros da API
export class ApiException extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiException';
    this.status = status;
    this.data = data;
  }
}

function getReadableErrorMessage(data: any, fallback: string): string {
  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  if (Array.isArray(data)) {
    const messages = data
      .map((item) => getReadableErrorMessage(item, ''))
      .filter(Boolean);

    if (messages.length) {
      return messages.join(', ');
    }
  }

  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }

    if (Array.isArray(data.message)) {
      const messages = data.message
        .map((item) => getReadableErrorMessage(item, ''))
        .filter(Boolean);

      if (messages.length) {
        return messages.join(', ');
      }
    }

    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error;
    }

    if (typeof data.title === 'string' && data.title.trim()) {
      return data.title;
    }
  }

  return fallback;
}

// Função para fazer requisições autenticadas
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  // Adicionar token de autenticação se não for skipAuth
  if (!skipAuth) {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    // Tratar token expirado (401)
    if (response.status === 401 && !skipAuth) {
      const refreshed = await refreshAuthToken();
      if (refreshed) {
        // Tentar novamente com o novo token
        const newToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...fetchOptions,
            headers,
          });
          return handleResponse<T>(retryResponse);
        }
      } else {
        // Refresh falhou, redirecionar para login
        clearAuth();
        window.location.href = '/';
        throw new ApiException('Sessão expirada. Por favor, faça login novamente.', 401);
      }
    }

    return handleResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiException) {
      throw error;
    }
    throw new ApiException(
      'Erro ao conectar com o servidor. Verifique sua conexão.',
      0
    );
  }
}

// Função para tratar resposta da API
async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  let data: any;
  
  if (response.status === 204) {
    // No content
    return null as T;
  }

  if (isJson) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const errorMessage = getReadableErrorMessage(
      data,
      `Erro ${response.status}: ${response.statusText}`
    );
    throw new ApiException(errorMessage, response.status, data);
  }

  return data as T;
}

/**
 * Upload com multipart/form-data (sem Content-Type manual — o browser define o boundary).
 * Recria o FormData em nova tentativa após refresh do token (401).
 */
export async function apiUploadRequest<T>(
  endpoint: string,
  method: 'PUT' | 'POST',
  createFormData: () => FormData
): Promise<T> {
  const doFetch = async () => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers,
      body: createFormData()
    });
  };

  let response = await doFetch();

  if (response.status === 401) {
    const refreshed = await refreshAuthToken();
    if (refreshed) {
      response = await doFetch();
    } else {
      clearAuth();
      window.location.href = '/';
      throw new ApiException('Sessão expirada. Por favor, faça login novamente.', 401);
    }
  }

  return handleResponse<T>(response);
}

/** DELETE autenticado sem corpo JSON (ex.: DELETE /users/avatar — não retorna stream de imagem). */
export async function apiDeleteRequest<T>(endpoint: string): Promise<T> {
  const doFetch = async () => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers
    });
  };

  let response = await doFetch();

  if (response.status === 401) {
    const refreshed = await refreshAuthToken();
    if (refreshed) {
      response = await doFetch();
    } else {
      clearAuth();
      window.location.href = '/';
      throw new ApiException('Sessão expirada. Por favor, faça login novamente.', 401);
    }
  }

  return handleResponse<T>(response);
}

// Função para refresh do token
async function refreshAuthToken(): Promise<boolean> {
  try {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      return false;
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ expToken: refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.authToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.expToken);
    return true;
  } catch {
    return false;
  }
}

// Função para limpar autenticação
export function clearAuth(): void {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
}

// Função para verificar se está autenticado
export function isAuthenticated(): boolean {
  return !!localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

// Função para obter token
export function getAuthToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}
